import { useCan } from '@features/auth';
import { type ClientTarget } from '@features/clients';
import { Badge, Button, Card, SelectField, StatTile, WriteError } from '@shared/ui';
import { useMemo, useState } from 'react';

import {
    readAllRows,
    useAutoRefresh,
    useBulkJob,
    useBulkJobRows,
    useRefresh,
} from '../hooks/use-bulk-migrations';
import { isActive, liveRunCount } from '../model/job-progress';
import { latestReportOf, rerunTag } from '../model/navigation';
import { OUTCOME_LABEL, outcomeFilterOptions, outcomeTotals } from '../model/outcome-copy';
import { exportDocument, exportFileName } from '../model/paging';

import { downloadJson } from './download';
import { JobProgressCard } from './JobProgressCard';
import { TenantRowsTable } from './TenantRowsTable';

import type { BulkModule, JobHeader } from '../schemas/bulk-migration';

/**
 * Step 4: the migration. What each client's run is doing, read by a refresh —
 * a `report` job the runner builds from the runs this launch started.
 *
 * The first refresh is asked for as soon as the launch is done, and then every
 * 30 s while any run is still going, by an engineer or above. A reader sees the
 * last report and no button.
 */
export function LaunchView({
    target,
    module,
    launch,
    jobs,
    now,
    memberTitles,
    onOpenFinalReport,
}: {
    target: ClientTarget;
    module: BulkModule;
    launch: JobHeader;
    jobs: readonly JobHeader[];
    now: number;
    /** A composite's member titles, for the tag on each record. */
    memberTitles?: ReadonlyMap<string, string> | undefined;
    onOpenFinalReport: () => void;
}) {
    const canRefresh = useCan('engineer');
    const refresh = useRefresh(target, module.moduleId, launch.reportId);
    const latestId = refresh.data?.reportId ?? latestReportOf(jobs, launch.reportId)?.reportId;
    const latest = useBulkJob(target, module.moduleId, latestId ?? null).data;
    // A refresh exists but its header has not loaded: say nothing rather than
    // "never refreshed", which would fire another one.
    const latestKnown = latestId === undefined || latest !== undefined;

    useAutoRefresh({
        canRefresh: canRefresh && latestKnown,
        launch,
        latestReport: latest,
        refresh,
    });

    const shown = latest !== undefined && !isActive(latest.state) ? latest : launch;
    const [outcome, setOutcome] = useState('all');
    const [downloading, setDownloading] = useState(false);
    const [downloadError, setDownloadError] = useState<Error | null>(null);
    const rows = useBulkJobRows(target, module.moduleId, shown, outcome);
    const loaded = useMemo(() => rows.data?.pages.flatMap((page) => page.rows) ?? [], [rows.data]);
    const totals = outcomeTotals(shown.counts);
    const live = liveRunCount(shown);

    const download = async () => {
        setDownloadError(null);
        setDownloading(true);
        try {
            const all = await readAllRows(target, module.moduleId, shown.reportId);
            downloadJson(exportFileName(shown), exportDocument(shown, all));
        } catch (error) {
            setDownloadError(error instanceof Error ? error : new Error(String(error)));
        } finally {
            setDownloading(false);
        }
    };

    return (
        <>
            {latest !== undefined && isActive(latest.state) && (
                <JobProgressCard job={latest} now={now} />
            )}

            <div className="mb-4 grid grid-cols-[repeat(auto-fit,minmax(150px,1fr))] gap-4">
                {[...totals]
                    .filter(([, count]) => count > 0)
                    .map(([key, count]) => (
                        <StatTile key={key} label={OUTCOME_LABEL[key]} value={String(count)} />
                    ))}
            </div>

            <Card>
                <div className="flex flex-wrap items-center gap-3 border-b border-line-subtle px-4 py-3">
                    <span className="text-[13px] font-semibold text-content-subtle">
                        {shown.mode === 'report'
                            ? `Status as of ${new Date(shown.finishedAt ?? shown.createdAt).toLocaleString()}`
                            : 'Launch result — not refreshed yet'}
                        {live > 0 ? ` · ${String(live)} still running` : ''}
                    </span>
                    {rerunTag(launch) !== null && <Badge tone="info">{rerunTag(launch)}</Badge>}
                    <div className="ml-auto w-[240px] [&_label]:sr-only">
                        <SelectField
                            label="Outcome"
                            value={outcome}
                            onChange={setOutcome}
                            options={outcomeFilterOptions(shown.counts)}
                        />
                    </div>
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={onOpenFinalReport}
                        disabled={isActive(launch.state)}
                    >
                        Final report
                    </Button>
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => void download()}
                        disabled={downloading}
                    >
                        {downloading ? 'Preparing…' : 'Download JSON'}
                    </Button>
                    {canRefresh && (
                        <Button
                            size="sm"
                            onClick={() => {
                                refresh.mutate();
                            }}
                            disabled={
                                refresh.isPending ||
                                launch.state !== 'done' ||
                                (latest !== undefined && isActive(latest.state))
                            }
                        >
                            {refresh.isPending ? 'Refreshing…' : 'Refresh'}
                        </Button>
                    )}
                </div>
                {refresh.error && (
                    <div className="px-4 pt-3">
                        <WriteError error={refresh.error} />
                    </div>
                )}
                {downloadError && (
                    <div className="px-4 pt-3">
                        <WriteError error={downloadError} />
                    </div>
                )}
                <TenantRowsTable
                    module={module}
                    layout="migration"
                    mode={shown.mode}
                    memberTitles={memberTitles}
                    rows={loaded}
                    isLoading={rows.isPending}
                    hasMore={rows.hasNextPage}
                    loadingMore={rows.isFetchingNextPage}
                    onLoadMore={() => void rows.fetchNextPage()}
                />
            </Card>
        </>
    );
}
